/* ============================================================
   Automatisches Magazin ab 200 EUR Warenwert.

   Produkt, Schwelle und Ein/Aus kommen aus den Theme-Einstellungen
   (Warenkorb, Gratisbeigabe), ausgegeben in snippets/custom-css-variables.liquid.
   Ohne Produkt: Magazin N°5 - The Evolution Issue, Variante 58036561510745 (SKU MV).
   Der Property-Wert 'ab_200_eur' bleibt auch bei anderer Schwelle, sonst
   erkennt das Skript bestehende Beigabe-Zeilen nicht mehr und legt eine zweite an.

   Die Schwelle wird aus dem Warenwert nach Positionsrabatten berechnet.
   Die automatisch hinzugefügte Magazin-Zeile zählt nicht zur Schwelle.
   Versandkosten zählen ebenfalls nicht.

   Wichtig: Dieses Skript verändert keinen Preis. Solange die Variante in
   Shopify 15,00 EUR kostet, bleibt dieser Preis auch im Warenkorb bestehen.
   Für eine Gratisbeigabe muss zusätzlich eine passende automatische
   Shopify-Rabattregel eingerichtet werden.
   ============================================================ */

(() => {
  'use strict';

  const config = (() => {
    try {
      return JSON.parse(document.getElementById('theme-brand-config')?.textContent || '{}').autoGift || {};
    } catch (error) {
      return {};
    }
  })();

  if (config.enabled === false) {
    return;
  }

  const THRESHOLD_CENTS = Number.isFinite(config.thresholdCents) ? config.thresholdCents : 20000;
  // Ohne Produkt in den Theme-Einstellungen bleibt es beim Magazin N°5.
  const MAGAZIN_VARIANT_ID = Number(config.variantId) || 58036561510745;
  const AUTO_PROPERTY_KEY = '_dreissigacker_auto_magazin';
  const AUTO_PROPERTY_VALUE = 'ab_200_eur';
  const EVENT_SOURCE = 'dreissigacker-auto-magazin';

  let running = false;
  let rerunRequested = false;
  let timer = null;

  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const endpoint = (path) => `${root()}${path}`;

  const isAutoMagazin = (item) =>
    Number(item.variant_id) === MAGAZIN_VARIANT_ID
    && item.properties
    && item.properties[AUTO_PROPERTY_KEY] === AUTO_PROPERTY_VALUE;

  const qualifyingSubtotal = (cart) =>
    (cart.items || []).reduce((sum, item) => {
      if (isAutoMagazin(item)) {
        return sum;
      }

      const linePrice = Number.isFinite(item.final_line_price)
        ? item.final_line_price
        : Number(item.line_price || 0);

      return sum + linePrice;
    }, 0);

  async function requestCart(path, body) {
    const response = await fetch(endpoint(path), {
      method: body ? 'POST' : 'GET',
      credentials: 'same-origin',
      headers: body
        ? {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          }
        : {
            Accept: 'application/json',
          },
      body: body ? JSON.stringify(body) : undefined,
    });

    if (!response.ok) {
      let message = `${response.status} ${response.statusText}`;

      try {
        const error = await response.json();
        message = error.description || error.message || message;
      } catch (error) {
        // Antwort war kein JSON. Der HTTP-Status reicht für das Logging.
      }

      throw new Error(message);
    }

    return response.json();
  }

  async function removeLine(item) {
    await requestCart('cart/change.js', {
      id: item.key,
      quantity: 0,
    });
  }

  async function setLineQuantity(item, quantity) {
    await requestCart('cart/change.js', {
      id: item.key,
      quantity,
    });
  }

  async function addMagazin() {
    await requestCart('cart/add.js', {
      items: [
        {
          id: MAGAZIN_VARIANT_ID,
          quantity: 1,
          properties: {
            [AUTO_PROPERTY_KEY]: AUTO_PROPERTY_VALUE,
          },
        },
      ],
    });
  }

  function refreshThemeCart() {
    if (window.themeVariables?.settings?.pageType === 'cart') {
      window.location.reload();
      return;
    }

    document.documentElement.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail: { source: EVENT_SOURCE },
      })
    );
  }

  async function syncAutoMagazin() {
    if (running) {
      rerunRequested = true;
      return;
    }

    running = true;
    let changed = false;

    try {
      const cart = await requestCart('cart.js');
      const autoLines = (cart.items || []).filter(isAutoMagazin);
      // NFT-Artikel sind digital. Bei reinen NFT-Warenkoerben kein physisches Magazin versenden.
      const hasNonNftProduct = (cart.items || []).some((item) =>
        !isAutoMagazin(item) && !/NFT/i.test(String(item.sku || ''))
      );
      const qualifies = hasNonNftProduct && qualifyingSubtotal(cart) >= THRESHOLD_CENTS;

      if (qualifies) {
        if (autoLines.length === 0) {
          await addMagazin();
          changed = true;
        } else {
          const [firstLine, ...duplicateLines] = autoLines;

          if (firstLine.quantity !== 1) {
            await setLineQuantity(firstLine, 1);
            changed = true;
          }

          for (const duplicateLine of duplicateLines) {
            await removeLine(duplicateLine);
            changed = true;
          }
        }
      } else {
        for (const autoLine of autoLines) {
          await removeLine(autoLine);
          changed = true;
        }
      }

      if (changed) {
        refreshThemeCart();
      }
    } catch (error) {
      console.warn('[Auto-Magazin] Warenkorb konnte nicht synchronisiert werden:', error);
    } finally {
      running = false;

      if (rerunRequested) {
        rerunRequested = false;
        scheduleSync(0);
      }
    }
  }

  function scheduleSync(delay = 80) {
    window.clearTimeout(timer);
    timer = window.setTimeout(syncAutoMagazin, delay);
  }

  /* Der Initiallauf braucht weder den kritischen Renderpfad noch zwei
     cart.js-Abrufe. Vorher startete DOMContentLoaded sofort einen Request und
     pageshow plante auf dem normalen Erstaufruf einen zweiten; traf der zweite
     den laufenden Request, setzte er rerunRequested und wiederholte die ganze
     Prüfung direkt danach. Jetzt läuft die erste Prüfung im Idle-Fenster und
     pageshow greift nur noch bei echter bfcache-Wiederherstellung. Warenkorb-
     Änderungen bleiben weiterhin sofort eventgetrieben. */
  function scheduleInitialSync() {
    /* Ohne Warenkorb gibt es nichts abzugleichen. Shopify setzt das
       cart-Cookie erst, wenn ein Warenkorb existiert; vorher fiel hier auf
       jeder Seite ein GET /cart.js an, auch fuer Besucher, die nur lesen. */
    if (!/(^|;\s*)cart=/.test(document.cookie)) return;

    const run = () => scheduleSync(0);

    /* Auf der Warenkorbseite laedt das Theme nach jeder Mengenaenderung neu,
       ohne cart:change. Im Idle-Fenster stand dort bis zu 1,5 s ein falscher
       Stand und "Zur Kasse" war klickbar (Magazin ohne Rabatt). */
    if (window.themeVariables?.settings?.pageType === 'cart') {
      run();
      return;
    }

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 1500 });
    } else {
      window.setTimeout(run, 600);
    }
  }

  document.addEventListener('cart:change', () => scheduleSync());
  document.addEventListener('cart:refresh', (event) => {
    if (event.detail?.source !== EVENT_SOURCE) {
      scheduleSync();
    }
  });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      scheduleSync(0);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleInitialSync, { once: true });
  } else {
    scheduleInitialSync();
  }
})();

/* ============================================================
   Safari: Hero-Video zuverlässig starten

   Die Video-Komponente des Themes setzt autoplay nur auf <video-media> und
   startet das eigentliche <video> per IntersectionObserver/JS. Gleichzeitig
   steht das Video auf preload="none". Safari ist bei genau dieser Kombination
   deutlich empfindlicher als Chromium: ein früher play()-Aufruf kann ohne
   späteren zweiten Versuch stehen bleiben.

   Zusätzlich liegt vor dem Shopify-MP4 eine selbst encodierte AV1-Quelle.
   Safari unterstützt AV1 nur auf einem Teil der Geräte zuverlässig. Für Safari
   nehmen wir beim Hero deshalb bewusst die universellere Shopify-MP4-Quelle.
   Andere Browser und Videos bleiben unverändert.

   Dieser Block lebt vorerst hier, weil auto-magazin.js bereits global und mit
   defer geladen wird. Bei der nächsten JS-Aufräumrunde in custom.js verschieben.
   ============================================================ */
(() => {
  'use strict';

  const isSafari = (() => {
    const ua = navigator.userAgent;
    return /Safari/i.test(ua)
      && !/(Chrome|Chromium|CriOS|FxiOS|EdgiOS|OPiOS|Android)/i.test(ua);
  })();

  if (!isSafari) {
    return;
  }

  // scope: der Theme-Editor rendert die Hero-Section einzeln neu, dann laeuft init fuer das neue Video erneut.
  const init = (scope = document) => {
    if (!document.body.classList.contains('template-index')) {
      return;
    }

    const media = document.querySelector('video-media[autoplay]');
    const video = media?.querySelector('video');

    if (!media || !video || !scope.contains(media) || media.closest('age-verifier-modal') || video.dataset.safariHero) {
      return;
    }

    video.dataset.safariHero = '1';
    const listeners = new AbortController();
    const { signal } = listeners;

    // Safari soll beim sichtbaren Hero nicht erst auf den JS-Observer warten.
    video.preload = 'auto';
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');

    // AV1 ist auf Safari hardware-/geräteabhängig. Die nachfolgenden Shopify-
    // Quellen sind MP4/H.264 und damit für den Hero die robustere Wahl.
    const av1Sources = Array.from(video.querySelectorAll('source')).filter((source) =>
      /av01/i.test(source.type || '')
    );

    if (av1Sources.length > 0) {
      av1Sources.forEach((source) => source.remove());
      video.load();
    }

    let retryTimer = null;

    const tryPlay = () => {
      if (!video.isConnected) {
        listeners.abort();
        return;
      }

      if (document.hidden || !video.paused) {
        return;
      }

      // BaseMedia blockiert play(), solange sein eigenes playing-Attribut noch
      // steht. Safari kann das Attribut über bfcache/Unterbrechungen behalten,
      // obwohl das native Video bereits pausiert ist.
      media.removeAttribute('playing');

      const attempt = video.play();
      if (attempt?.catch) {
        attempt.catch(() => {
          window.clearTimeout(retryTimer);
          retryTimer = window.setTimeout(() => {
            if (video.isConnected && !document.hidden && video.paused) {
              video.load();
              video.play()?.catch(() => {});
            }
          }, 350);
        });
      }
    };

    // Sofortiger Start plus Wiederanlauf, wenn Safari nach Tabwechsel, bfcache
    // oder dem Altersdialog das Video angehalten hat.
    tryPlay();
    video.addEventListener('canplay', tryPlay, { signal });
    window.addEventListener('pageshow', () => window.setTimeout(tryPlay, 0), { signal });
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        window.setTimeout(tryPlay, 0);
      }
    }, { signal });

    /* Vorher wurde nach JEDEM Klick auf der Startseite ein 700-ms-Retry-Timer
       gesetzt, obwohl nur das Schließen des Altersdialogs relevant ist. Das
       Theme liefert dafür bereits dialog:after-hide; so bleibt der Retry exakt
       auf diesen Zustandswechsel begrenzt. */
    document.addEventListener('dialog:after-hide', (event) => {
      if (event.target?.matches?.('age-verifier-modal')) {
        window.setTimeout(tryPlay, 0);
      }
    }, { capture: true, signal });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
